import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { LinearScale } from '@material-ui/icons';
import { Button, Grow, Box, Card, Tooltip, IconButton, Typography, Input } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import SimpleInfoDialog from '../dialog/SimpleInfoDialog';
import { createPortal } from 'react-dom';

/**
 * @function GenerateTransectsStep
 * @description The transect settings, shown over the step panel they belong to.
 * The panel is drawn into the steps overlay and takes its place in the column, the way the
 * baseline step's own panel does. It used to be positioned absolutely - centred on the overlay,
 * then anchored to its top - and that is what kept cutting it off: an element taken out of the flow
 * adds nothing to the column's scroll height, so whatever did not fit was simply unreachable, above
 * the top edge or below the bottom one depending on where it was pinned. In the flow the column
 * measures it, scrolls to it, and no part of it can land outside.
 * @returns {JSX.Element} Coastline Analysis steps JSX element
 */
const GenerateTransectsStep = ({
  t,
  onNext,
  onPrevious,
  isCurrent,
  transectsExtent,
  setTransectsExtent,
  transectsStep,
  setTransectsStep,
  transectsObjects,
  setTransectsObjects,
  generateTransectsPreview,
  readTransectsFileData,
  threshold,
  setThreshold,
  confirmTransectsConfigEnabled,
  overlayTarget,
}) => {
  const dispatch = useDispatch();
  const [isSettingsTransectOpen, setIsSettingsTransectOpen] = useState(false);
  const [showImageChooserHelp, setShowImageChooserHelp] = useState(false);

  useEffect(() => {
    if (!isCurrent || !isSettingsTransectOpen) return;
    setTransectsExtent(1100);
    setTransectsStep(400);
    generateTransectsPreview();
  }, [isCurrent, isSettingsTransectOpen]);
  return (
    <>
      {isSettingsTransectOpen &&
        overlayTarget.current &&
        createPortal(
          <Grow in={isSettingsTransectOpen} unmountOnExit>
            <Card
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
                padding: 12,
                textAlign: 'center',
                pointerEvents: 'auto',
              }}
              elevation={3}>
              <div
                style={{
                  height: 20,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}>
                <Tooltip title={t('forms.imageChooser.helpTooltip')}>
                  <IconButton onClick={() => setShowImageChooserHelp(true)} size="small" style={{ padding: 4 }}>
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <SimpleInfoDialog
                  open={showImageChooserHelp}
                  close={() => {
                    setShowImageChooserHelp(false);
                  }}
                  title={t('forms.transectEvolution.title')}
                  text={t('forms.transectEvolution.TransectEvolutionInfoDialog')}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 20,
                }}>
                <Typography id="discrete-slider" gutterBottom style={{ fontSize: 15 }}>
                  {t('forms.shorelineAnalysis.transectsSettings.setStepLabel')}
                </Typography>
                <Input
                  type="number"
                  value={transectsStep === 0 ? '' : transectsStep}
                  onChange={(e) => {
                    let value = Number(e.target.value);
                    setTransectsStep(isNaN(value) ? 0 : Number(value));
                  }}
                  onBlur={(e) => {
                    let value = Number(e.target.value);
                    generateTransectsPreview();
                  }}
                  placeholder="200"
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 20,
                }}>
                <Typography id="discrete-slider" gutterBottom style={{ fontSize: 15 }}>
                  {t('forms.shorelineAnalysis.transectsSettings.setExtentLabel')}
                </Typography>
                <Input
                  type="number"
                  value={transectsExtent === 0 ? '' : transectsExtent}
                  onChange={(e) => {
                    let value = Number(e.target.value);
                    setTransectsExtent(isNaN(value) ? 0 : value);
                  }}
                  onBlur={(e) => generateTransectsPreview()}
                  placeholder="500"
                />
              </div>
              <p
                style={{
                  padding: 0,
                  margin: 0,
                }}>
                {t('forms.shorelineAnalysis.transectsSettings.or')}
              </p>
              <Button color="secondary" component="label" htmlFor="file_import_transects_kml">
                {t('forms.shorelineAnalysis.transectsSettings.importTransectsStepsExtent')}
                <input
                  style={{ display: 'none' }}
                  id="file_import_transects_kml"
                  accept=".kml"
                  type="file"
                  onClick={(e) => (e.target.value = null)}
                  onChange={readTransectsFileData}
                />
              </Button>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 20,
                }}>
                <Typography id="discrete-slider" gutterBottom style={{ fontSize: 15 }}>
                  {t('forms.shorelineAnalysis.transectsSettings.setThresholdLabel')}
                </Typography>
                <Input
                  type="number"
                  value={threshold}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (value === '') return setThreshold('');
                    value = Number(value);
                    setThreshold(isNaN(value) ? 0 : value);
                  }}
                  placeholder="0.01"
                />
              </div>
              <Button
                disabled={!confirmTransectsConfigEnabled}
                color="primary"
                onClick={async () => {
                  if (threshold === '') setThreshold(0.01);
                  setIsSettingsTransectOpen(false);
                  transectsObjects.forEach((object) => object.setMap(null));
                  setTransectsObjects([]);
                  onNext();
                }}
                style={{
                  paddingBottom: 0,
                  marginBottom: 0,
                }}>
                {t('forms.shorelineAnalysis.transectsSettings.confirm')}
              </Button>
              <Button
                color="secondary"
                style={{
                  padding: 0,
                  margin: 0,
                }}
                onClick={() => {
                  transectsObjects.forEach((object) => object.setMap(null));
                  setTransectsObjects([]);
                  onPrevious();
                  setIsSettingsTransectOpen(false);
                }}>
                {t('forms.csqueeze.result.cancel')}
              </Button>
            </Card>
          </Grow>,
          overlayTarget.current,
        )}
      <Button
        style={{ justifyContent: 'left' }}
        disabled={!isCurrent}
        color="primary"
        startIcon={<LinearScale />}
        onClick={() => setIsSettingsTransectOpen(true)}>
        {t('forms.csqueeze.result.configTransects')}
      </Button>
    </>
  );
};

export default GenerateTransectsStep;
